import { useEffect, useState } from 'react';

import './css/Modals.css';

interface ExitIntentModalProps {
  isOpen: boolean;
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel: string;
  primaryId: string;
  secondaryId: string;
  onPrimary: () => void;
  onSecondary: () => void;
}

/**
 * VI-548 exit-intent modal.
 *
 * Per the ticket AC: no X button, clicking the backdrop must NOT close, and
 * pressing Escape must NOT close. The user is forced to choose one of the two
 * CTAs to dismiss.
 */
const ExitIntentModal = ({
  isOpen,
  title,
  body,
  primaryLabel,
  secondaryLabel,
  primaryId,
  secondaryId,
  onPrimary,
  onSecondary,
}: ExitIntentModalProps) => {
  const [isMounted, setIsMounted] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<'primary' | 'secondary' | null>(null);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setIsClosing(false);
    } else if (isMounted) {
      setIsClosing(true);
      const timer = window.setTimeout(() => setIsMounted(false), 300);
      return () => window.clearTimeout(timer);
    }
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isMounted) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isMounted]);

  if (!isMounted) return null;

  return (
    <div
      className={`modal-backdrop ${isClosing ? 'modal-fadeOut' : ''}`}
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      style={{
        alignItems: 'flex-start',
        paddingTop: 'clamp(1rem, 6vh, 3rem)',
      }}
    >
      <div
        className={`modal-dialog ${isClosing ? 'modal-fadeOut' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="exit-intent-modal-title"
        aria-describedby="exit-intent-modal-body"
        style={{ maxWidth: '620px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ borderRadius: '8px' }}>
          <div className="modal-header" style={{ padding: '0.85rem 1.5rem' }}>
            <h5
              id="exit-intent-modal-title"
              className="modal-title"
              style={{
                fontWeight: 500,
                fontSize: '1.15rem',
                color: '#181C32',
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {title}
            </h5>
          </div>

          <div
            className="modal-body"
            style={{ padding: '1.25rem 1.5rem' }}
          >
            <p
              id="exit-intent-modal-body"
              style={{
                fontSize: '0.95rem',
                fontWeight: 400,
                color: '#495057',
                lineHeight: 1.8,
                margin: 0,
              }}
            >
              {body}
            </p>
          </div>

          <div
            className="modal-footer"
            style={{
              padding: '0.75rem 1.5rem',
              gap: '0.5rem',
            }}
          >
            <button
              id={secondaryId}
              onClick={onSecondary}
              onMouseEnter={() => setHoveredButton('secondary')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#1E6DBB',
                fontSize: '0.95rem',
                fontWeight: 400,
                cursor: 'pointer',
                padding: '0.6rem 0.85rem',
                borderRadius: '6px',
                backgroundColor:
                  hoveredButton === 'secondary' ? '#F2F7FD' : 'transparent',
                transition: 'background-color 0.2s ease, color 0.2s ease',
              }}
            >
              {secondaryLabel}
            </button>

            <button
              id={primaryId}
              onClick={onPrimary}
              onMouseEnter={() => setHoveredButton('primary')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                background: hoveredButton === 'primary' ? '#1A5FA5' : '#1E6DBB',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '0.95rem',
                fontWeight: 400,
                cursor: 'pointer',
                padding: '0.6rem 1.1rem',
                transition: 'background-color 0.2s ease',
              }}
            >
              {primaryLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentModal;
